import React, { useEffect, useState, useRef } from 'react';
import camelCase from 'lodash.camelcase';
import classnames from 'classnames';
import snakeCase from 'lodash.snakecase';
import classes from './classes.module.scss';
import { asyncLoadScript, getTextColorForTheme } from '../../config/utils';
import { addCreditCardFormInputTypes, getAddCreditCardFormErrorsText, getPlaceHolders } from '../add_credit_card_form_utils';
import { LOG_LEVELS } from '../../config/app_logger';

const objectToSnakeCase = (obj) => {
  if (!obj || typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) return obj.map(item => objectToSnakeCase(item));
  const result = {};
  Object.keys(obj).forEach(key => {
    result[snakeCase(key)] = objectToSnakeCase(obj[key]);
  });
  return result;
};

const mapFlowTokenizeResult = (result) => {
  const { data } = result;
  return {
    token: data.token,
    scheme: data.scheme,
    last4: data.last4,
    expiry_month: data.expiry_month,
    expiry_year: data.expiry_year,
    bin: data.bin,
    card_type: data.type
  };
};

const useGetewayCheckoutCom = ({
  config, log, getConfig, submitCardDataHandler, loading, blueSnapSettings, t,
  setSavingBlueSnapCard
}) => {
  const { publicKey } = config;
  const isFlowMode = !!config.paymentSession;

  const [jsReady, setJsReady] = useState(false);
  const [checkoutErrors, setCheckoutErrors] = useState({});
  const [status, setStatus] = useState({
    setupComplete: false, addCardClicked: false
  });

  const flowCardRef = useRef(null);

  useEffect(() => {
    if (Object.keys(config || {}).length > 0) {
      const isCheckoutComScript = config.jsSrc.includes('checkout.com');
      if (isCheckoutComScript) {
        if (isFlowMode) {
          asyncLoadScript(config.jsSrc, window.CheckoutWebComponents).then(() => {
            setJsReady(true);
          });
        } else {
          asyncLoadScript(config.jsSrc, window.Frames).then(() => {
            setJsReady(true);
          });
        }
      }
    }
  }, [config]);

  const setStatusHandler = (value) => {
    setStatus(pendingStatus => ({ ...pendingStatus, ...value }));
  };

  const addCreditCardFormErrorsText = getAddCreditCardFormErrorsText(t);

  const checkoutInputTypes = {
    cardNumber: 'ccn',
    cvv: 'cvv',
    expiryDate: 'exp'
  };

  const setErrors = (errorsKeys) => {
    setCheckoutErrors(pendingCheckoutErrors => {
      let errors = {};
      errorsKeys.forEach((key) => {
        errors = { ...errors, [checkoutInputTypes[key]]: addCreditCardFormErrorsText[key] };
      });
      setStatusHandler({ getewayErrors: { ...pendingCheckoutErrors, ...errors } });
      return { ...pendingCheckoutErrors, ...errors };
    });
  };

  const clearedErrors = (errorType) => {
    setCheckoutErrors(pendingCheckoutErrors => {
      const clearedErrorKeys = Object.keys(pendingCheckoutErrors)
        .filter((err) => err !== errorType);
      const clearedError = {};
      clearedErrorKeys.forEach((key) => { clearedError[key] = pendingCheckoutErrors[key]; });
      setStatusHandler({ getewayErrors: { ...clearedError } });
      return { ...clearedError };
    });
  };

  useEffect(() => {
    const tokenExpiredErrorKey = Object.keys(checkoutErrors)
      .find((k) => checkoutErrors[k] === checkoutErrors.tokenExpired);
    if (tokenExpiredErrorKey) {
      clearedErrors(tokenExpiredErrorKey);
      getConfig();
    }
  }, [checkoutErrors]);

  const setAddCardClicked = () => {
    setStatusHandler({ addCardClicked: true });
  };

  // ── Flow mode: add card handler ──
  const addCardHandleFlow = ({
    handleNameZipError, name, zip, setAddCreditCardFormErrorsHandler, isloading, getewayItem
  }) => {
    if (Object.keys(checkoutErrors).length > 0 || isloading || !getewayItem) return;
    setSavingBlueSnapCard(true);
    const isNameZipValid = handleNameZipError({
      name,
      zip,
      setErrors: setAddCreditCardFormErrorsHandler,
      addCreditCardFormInputTypes,
      addCreditCardFormErrorsText
    });
    if (!isNameZipValid) {
      setSavingBlueSnapCard(false);
      return;
    }
    log(LOG_LEVELS.INFO, 'CC Checkout Flow', 'Tokenizing card');
    getewayItem.tokenize().then((result) => {
      const cardData = mapFlowTokenizeResult(result);
      setStatusHandler({ cardData });
      submitCardDataHandler({ cardData, name, zip });
    }).catch((e) => {
      log(LOG_LEVELS.ERROR, 'CC Checkout Flow', 'Tokenize failed', e);
      if (e && e.code === 'payment_session_expired') {
        getConfig();
      }
      setSavingBlueSnapCard(false);
    });
  };

  // ── Frames mode: add card handler ──
  const checkNoValidCheckoutFields = () => {
    const checkoutInputClassNames = {
      cardNumber: 'card-number-frame',
      cvv: 'cvv-frame',
      expiryDate: 'expiry-date-frame'
    };
    const noValidCheckoutFields = [];
    Object.keys(checkoutInputClassNames).forEach((key) => {
      const intupClassName = document.getElementsByClassName(checkoutInputClassNames[key])[0].className;
      if (!intupClassName.includes('frame--valid')) {
        noValidCheckoutFields.push(key);
      }
    });
    return noValidCheckoutFields;
  };

  const addCardHandleFrames = ({
    handleNameZipError, name, zip, setAddCreditCardFormErrorsHandler, isloading, getewayItem
  }) => {
    if (Object.keys(checkoutErrors).length > 0 || isloading || !getewayItem) return;
    setSavingBlueSnapCard(true);
    const isNameZipValid = handleNameZipError({
      name,
      zip,
      setErrors: setAddCreditCardFormErrorsHandler,
      addCreditCardFormInputTypes,
      addCreditCardFormErrorsText
    });
    log(LOG_LEVELS.INFO, 'CC Checkout', 'on Saving credit card');
    if (!getewayItem.isCardValid()) {
      setErrors(checkNoValidCheckoutFields());
      setSavingBlueSnapCard(false);
      return;
    }
    getewayItem.submitCard(publicKey).then((cardData) => {
      if (!isNameZipValid) {
        setSavingBlueSnapCard(false);
        return;
      }
      setStatusHandler({ cardData });
      getewayItem.cardholder = {
        name,
        billingAddress: {
          zip
        }
      };
      submitCardDataHandler({ cardData, name, zip });
    }).catch((e) => {
      setErrors(e);
      setSavingBlueSnapCard(false);
    });
  };

  // ── Flow mode initialization ──
  useEffect(() => {
    if (!isFlowMode || loading || !jsReady || !window.CheckoutWebComponents) return () => {};
    log(LOG_LEVELS.INFO, 'CC Checkout Flow', 'Initializing Flow card component');

    let cancelled = false;
    let cardComponent = null;

    const initFlow = async () => {
      try {
        const { paymentSession: ps, environment } = config;
        const paymentSessionOriginal = objectToSnakeCase(ps);
        const checkout = await window.CheckoutWebComponents({
          paymentSession: paymentSessionOriginal,
          publicKey,
          environment,
          locale: 'en'
        });

        cardComponent = checkout.create('card', {
          showPayButton: false,
          displayCardholderName: 'hidden'
        });

        await cardComponent.isAvailable();

        if (cancelled) return;

        cardComponent.mount('#flow-card-container');
        flowCardRef.current = cardComponent;

        setStatusHandler({
          setupComplete: true,
          addCardHandle: addCardHandleFlow,
          setAddCardClicked,
          getewayItem: cardComponent
        });
      } catch (err) {
        log(LOG_LEVELS.ERROR, 'CC Checkout Flow', 'Flow init failed', err);
        if (!cancelled) {
          setStatusHandler({ setupComplete: false });
        }
      }
    };

    initFlow();

    return () => {
      cancelled = true;
      if (flowCardRef.current) {
        try { flowCardRef.current.unmount(); } catch (_) { /* ignore */ }
        flowCardRef.current = null;
      }
      setStatus({});
    };
  }, [jsReady, publicKey, config, isFlowMode]);

  // ── Frames mode initialization ──
  useEffect(() => {
    if (isFlowMode) return () => {};

    const defaultCheckoutStyle = {
      base: {
        fontSize: '16px',
        fontWeight: 700,
        height: '100%',
        width: '100%',
        backgroundColor: 'transparent',
        borderRadius: 0,
        padding: 0,
        color: getTextColorForTheme()
      },
      autofill: {
        color: getTextColorForTheme()
      },
      hover: {
        color: getTextColorForTheme()
      },
      focus: {
        color: getTextColorForTheme()
      },
      valid: {
        color: getTextColorForTheme()
      },
      invalid: {
        color: getTextColorForTheme()
      },
      placeholder: {
        base: {
          fontSize: '16px',
          fontWeight: 400
        }
      }
    };
    if (loading || !jsReady || !window.Frames) return () => {};
    log(LOG_LEVELS.INFO, 'CC Checkout', 'creating hostedPaymentFields');
    window.Frames.init({
      publicKey,
      localization: {
        cardNumberPlaceholder: getPlaceHolders({
          placeHolders: blueSnapSettings.placeHolders,
          placeHolderType: checkoutInputTypes.cardNumber
        }),
        cvvPlaceholder: getPlaceHolders({
          placeHolders: blueSnapSettings.placeHolders, placeHolderType: checkoutInputTypes.cvv
        }),
        expiryMonthPlaceholder: getPlaceHolders({
          placeHolders: blueSnapSettings.placeHolders,
          placeHolderType: checkoutInputTypes.expiryDate
        }).split('/')[0] || ' ',
        expiryYearPlaceholder: getPlaceHolders({
          placeHolders: blueSnapSettings.placeHolders,
          placeHolderType: checkoutInputTypes.expiryDate
        }).split('/')[1] || ' '
      },
      style: blueSnapSettings.checkoutStyle || defaultCheckoutStyle
    });
    window.Frames.addEventHandler(window.Frames.Events.READY, () => {
      setStatusHandler({ setupComplete: true });
    });
    window.Frames.addEventHandler(window.Frames.Events.FRAME_ACTIVATED, () => {
      setStatusHandler({ setupComplete: true });
    });
    window.Frames.addEventHandler(window.Frames.Events.CARD_BIN_CHANGED, ({ scheme }) => {
      setStatusHandler({ cardNameType: scheme });
    });
    window.Frames.addEventHandler(window.Frames.Events.FRAME_BLUR, () => {
      setStatusHandler({ fieldFocus: '' });
    });
    window.Frames.addEventHandler(window.Frames.Events.FRAME_FOCUS, ({ element }) => {
      const camelCaseElement = camelCase(element);
      setStatusHandler({ fieldFocus: checkoutInputTypes[camelCaseElement] });
    });
    window.Frames.addEventHandler(window.Frames.Events.FRAME_VALIDATION_CHANGED, ({
      element, isValid
    }) => {
      const camelCaseElement = camelCase(element);
      if (!isValid) {
        setErrors([camelCaseElement]);
      }
      if (isValid) {
        clearedErrors(checkoutInputTypes[camelCaseElement]);
      }
    });
    const blueSnapStatusProps = {
      addCardHandle: addCardHandleFrames, setAddCardClicked, getewayItem: window.Frames
    };
    setStatusHandler(blueSnapStatusProps);

    return () => {
      window.Frames.removeAllEventHandlers(window.Frames.Events.READY);
      window.Frames.removeAllEventHandlers(window.Frames.Events.CARD_BIN_CHANGED);
      window.Frames.removeAllEventHandlers(window.Frames.Events.FRAME_BLUR);
      window.Frames.removeAllEventHandlers(window.Frames.Events.FRAME_FOCUS);
      window.Frames.removeAllEventHandlers(window.Frames.Events.FRAME_VALIDATION_CHANGED);
      setStatus({});
    };
  }, [jsReady, publicKey, config, isFlowMode]);

  const getInputClassName = (inputClassName) => classnames(inputClassName, classes.checkoutDiv);

  // Flow mode: single container div
  const flowCardContainer = (
    <div id="flow-card-container" className={ classes.checkoutDiv } />
  );

  // Frames mode: three hosted-field divs
  const cardNumberInput = (
    <div className={ getInputClassName('card-number-frame') } />
  );
  const dateExpiredInput = (
    <div className={ getInputClassName('expiry-date-frame') } />
  );
  const securityInput = (
    <div className={ getInputClassName('cvv-frame') } />
  );
  const fraudFrame = (
    <></>
  );

  const getewayFields = isFlowMode
    ? { flowCardContainer, fraudFrame, isFlowMode: true }
    : {
      cardNumberInput, dateExpiredInput, securityInput, fraudFrame, isFlowMode: false
    };

  return { getewayFields, getewayStatus: status };
};

export default useGetewayCheckoutCom;
